import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Errors, { getString } from "../../../components/errors/Errors";
import Listview from "../../../components/listview/Listview";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import useAbortController from "../../../hooks/useAbortController";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Administration } from "../../../listsSettings/administration";
import { ErrorCode, IAction, IListviewField, SearchResult, UserPermissions } from "../../../services/types";
import { getCurrentUrlAsSource, getSelectedItems, getSourceFromUrl } from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Add from "../../../svg/Add.svg?react";
import Bin from "../../../svg/Bin.svg?react";
import Edit from "../../../svg/Edit.svg?react";
import * as Fields from "./Fields";
import { PermissionFull } from "./types";
import { editForm, newForm } from "./urls";
import { deletePermissions, getPermissionFilters, getPermissions } from "./webapi";

const AllItems: FC = () => {
  const [abortController, resetAbortController] = useAbortController();
  const listviewId = "permissions-all-items";
  const fields: IListviewField<PermissionFull>[] = [Fields.name, Fields.description];

  const statusBar = useStatusBarState();
  const [permissions, setPermissions] = useState<SearchResult<PermissionFull> | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode>();

  const [userPermissions, setUserPermissions] = useState<UserPermissions>();
  const navigateTo = useNavigate();

  const pagination = getPagination(listviewId) || {
    ...DefaultPagination,
  };

  useEffect(() => {
    loadPermissions(pagination);

    return () => {
      abortController && abortController.abort();
    };
  }, []);

  const loadPermissions = async (pagination: Pagination) => {
    const abortController = resetAbortController();
    const userPermissions = await getUserPermissions(
      Administration.Lists.Permissions.InternalName,
      Administration.InternalName,
      abortController.signal,
    );
    const canRead = userPermissions.schemaPermission && userPermissions.listPermissions.includes("read-all");
    if (!abortController.signal.aborted) {
      setUserPermissions(userPermissions);
      !canRead && setErrorCode(403);
      if (canRead) {
        const result = await getPermissions(pagination, abortController.signal);
        if (!abortController.signal.aborted) {
          result.data && setPermissions(result.data);
          result.error && setErrorCode(result.error.code);
        }
      }
    }
  };

  const newAction: IAction = {
    title: strings.new,
    icon: Add,

    onClick: () => navigateTo(`${newForm}?${getCurrentUrlAsSource()}`),
    disabled: !userPermissions?.listPermissions.includes("create"),
  };
  const editAction: IAction = {
    title: strings.edit,
    icon: Edit,
    onClick: () => {
      const selectedItem = getSelectedItems(permissions)[0];
      selectedItem && navigateTo(getSourceFromUrl() ?? `${editForm}/${selectedItem.name}`);
    },
    disabled: getSelectedItems(permissions).length !== 1 || !userPermissions?.listPermissions.includes("edit-all"),
  };
  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: async () => {
      const selectedItems = getSelectedItems(permissions);
      if (selectedItems.length > 0 && confirm(strings.confirmDeleteItems)) {
        const abortController = resetAbortController();
        const result = await deletePermissions(
          selectedItems.map((x) => x.name),
          abortController.signal,
        );

        if (result.error) {
          statusBar.addError(getString(result.error));
        } else if (result.data) {
          const s = selectedItems.filter((x) => x.name && result.data?.[x.name]);

          if (s.length != selectedItems.length) {
            statusBar.addWarning(strings.notAllElementsDeleted);
          }
          if (s.length > 0) {
            await loadPermissions(pagination);
          }
        }
      }
    },
    disabled: getSelectedItems(permissions).length === 0 || !userPermissions?.listPermissions.includes("delete-all"),
  };

  const actions = [newAction, editAction, deleteAction];

  return (
    <>
      {userPermissions &&
        (!errorCode ? (
          <Listview
            id={listviewId}
            actions={actions}
            viewProperties={{
              schema: Administration.InternalName,
              list: Administration.Lists.Permissions.InternalName,
              name: "all_items",
              setListAndPage: true,
            }}
            fields={fields}
            allowSelectRows
            reload={loadPermissions}
            searchResult={permissions}
            getFilters={getPermissionFilters}
            pagination={pagination}
            updateItems={(items) =>
              permissions &&
              setPermissions({
                ...permissions,
                items: items,
              })
            }
          />
        ) : (
          errorCode && <Errors errorCode={errorCode} />
        ))}
    </>
  );
};

export default AllItems;
